import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  format,
  isSameMonth,
  isSameDay,
} from 'date-fns';
import { useRef } from 'react';

import { CalendarEvent } from '../stores/useCalendarStore';

import { useContainerHeight } from '@/common/hooks/useContainerHeight';
import { useContainerWidth } from '@/common/hooks/useContainerWidth';
import { useDateLocale } from '@/common/hooks/useDateLocale';

interface CalendarGridProps {
  currentDate: Date;
  events: CalendarEvent[];
  onDateClick: (date: Date) => void;
}

export const CalendarGrid = ({ currentDate, events, onDateClick }: CalendarGridProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const containerWidth = useContainerWidth(containerRef);
  const containerHeight = useContainerHeight(containerRef);
  const { locale } = useDateLocale();

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(monthStart);
  const calendarStart = startOfWeek(monthStart);
  const calendarEnd = endOfWeek(monthEnd);

  const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  const weekDays = [0, 1, 2, 3, 4, 5, 6].map((day) =>
    format(new Date(2024, 0, 7 + day), 'EEE', { locale })
  );

  // Calculate responsive sizes based on container dimensions
  const calculateResponsiveSizes = () => {
    const minCellSize = 32; // Minimum size for a day cell
    const maxCellSize = 64; // Maximum size for a day cell

    // Calculate cell size based on container width (7 columns)
    const cellSizeFromWidth = Math.floor((containerWidth - 24) / 7); // 24px for gaps
    // Calculate cell size based on container height (7 rows - 6 for days + 1 for weekdays)
    const cellSizeFromHeight = Math.floor((containerHeight - 40) / 7); // 40px for weekdays and gaps

    // Use the smaller of the two sizes to maintain aspect ratio
    const cellSize = Math.max(
      minCellSize,
      Math.min(maxCellSize, Math.min(cellSizeFromWidth, cellSizeFromHeight))
    );

    // Calculate font sizes based on cell size
    const dayFontSize = Math.max(12, Math.min(16, Math.floor(cellSize / 3)));
    const weekDayFontSize = Math.max(11, Math.min(14, Math.floor(cellSize / 4)));

    return {
      cellSize,
      dayFontSize,
      weekDayFontSize,
      dotSize: Math.max(4, Math.min(6, Math.floor(cellSize / 10))),
    };
  };

  const { cellSize, dayFontSize, weekDayFontSize, dotSize } = calculateResponsiveSizes();

  return (
    <div ref={containerRef} className="w-full">
      <div className="grid grid-cols-7 gap-1 mb-1">
        {weekDays.map(day => (
          <div
            key={day}
            className="text-center font-medium text-gray-500 py-2"
            style={{ fontSize: `${weekDayFontSize}px` }}
          >
            {day}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {days.map(day => {
          const hasEvent = events.some(event => isSameDay(event.event_date, day));
          const isToday = isSameDay(day, new Date());
          return (
            <button
              key={day.toISOString()}
              className={`
                rounded-lg text-sm relative flex items-center justify-center
                ${isSameMonth(day, currentDate)
                  ? 'text-gray-900'
                  : 'text-gray-400'
                }
                ${hasEvent
                  ? 'bg-blue-50 hover:bg-blue-100'
                  : 'hover:bg-gray-50'
                }
              `}
              style={{
                height: `${cellSize}px`,
                fontSize: `${dayFontSize}px`,
              }}
              onClick={() => onDateClick(day)}
            >
              <span className={`
                flex items-center justify-center
                ${isToday
                  ? 'bg-blue-500 text-white rounded-full'
                  : ''
                }
              `}
              style={{
                width: isToday ? `${Math.floor(cellSize * 0.75)}px` : 'auto',
                height: isToday ? `${Math.floor(cellSize * 0.75)}px` : 'auto',
              }}>
                {format(day, 'd', { locale })}
              </span>
              {hasEvent && (
                <div
                  className="absolute bottom-1 left-1/2 -translate-x-1/2 bg-blue-500 rounded-full"
                  style={{
                    width: `${dotSize}px`,
                    height: `${dotSize}px`,
                  }}
                />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};
